import { Close, Psychology } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Typography
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { FiltersState } from "../../common/hooks/useFilters";
import { getUserTimezone } from "../../utils/timezone";
import CoachingAttributeCard from "./components/CoachingAttributeCard";
import CoachingConfiguration from "./components/CoachingConfiguration";
import ContextWarningAlert from "./components/ContextWarningAlert";
import DuplicateConfigurationDialog from "./components/DuplicateConfigurationDialog";
import { useAttributeExamples } from "./hooks/useAttributeExamples";
import { CoachingArtifact } from "./hooks/useCoachingArtifacts";
import { useCoachingHubPrompt } from "./hooks/useCoachingHubPrompt";
import { useCoachingContextStore } from "./store/coachingContextStore";
import { useCoachingGeneratorStore } from "./store/coachingGeneratorStore";
import { generateDefaultCoachingPrompt } from "./utils/prompts";

interface GenerateInsightsDrawerProps {
  open: boolean;
  onClose: () => void;
  onArtifactGenerated?: (artifactId: string) => void;
  filters: FiltersState;
  permittedEmails: string[] | null;
  tenantId: string | null;
}

const GenerateInsightsDrawer = ({
  open,
  onClose,
  onArtifactGenerated,
  filters,
  permittedEmails,
  tenantId
}: GenerateInsightsDrawerProps) => {
  const timezone = getUserTimezone();
  const queryClient = useQueryClient();
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateArtifact, setDuplicateArtifact] = useState<CoachingArtifact | null>(null);

  const {
    selectedAttributes,
    isGeneratingInsights,
    error,
    customPrompt,
    includeAttributeStats,
    includeLensDefinition,
    includeTeamBestPractices,
    temperature,
    existingArtifacts,
    setCustomPrompt,
    setTemperature,
    selectAllAttributes,
    clearSelectedAttributes,
    setError,
    generateInsights
  } = useCoachingGeneratorStore();

  const {
    data: attributeExamples,
    isLoading,
    error: fetchError
  } = useAttributeExamples({
    timeRange: filters.timeRange,
    timezone,
    lensFilter: filters.protocol || "all",
    userFilter: filters.user,
    permittedEmails,
    tenantId,
    enabled: !!filters.protocol && filters.protocol !== "all"
  });

  const {
    attributeStatsContent,
    lensDefinitionContent,
    teamBestPracticesContent,
    fetchAllContext,
    getIsLoadingContext,
    getHasRequiredContext,
    getMissingContextNames
  } = useCoachingContextStore();

  const isLoadingContext = getIsLoadingContext();
  const hasRequiredContext = getHasRequiredContext();
  const missingContextNames = getMissingContextNames();
  const showContextUnavailableWarning = !isLoadingContext && !hasRequiredContext;

  useEffect(() => {
    if (!filters.protocol || filters.protocol === "all") {
      return;
    }

    fetchAllContext({
      timeRange: filters.timeRange,
      protocol: filters.protocol,
      protocolPath: undefined,
      user: filters.user,
      permittedEmails,
      timezone,
      tenantId,
      includeAttributeStats,
      includeLensDefinition,
      includeTeamBestPractices
    });
  }, [
    filters.timeRange,
    filters.protocol,
    filters.user,
    permittedEmails,
    timezone,
    tenantId,
    includeAttributeStats,
    includeLensDefinition,
    includeTeamBestPractices,
    fetchAllContext
  ]);

  const {
    coachingLensPrompt,
    coachingLensTitle,
    coachingLensModel,
    coachingLensTemperature,
    coachingLensMaxTokens
  } = useCoachingHubPrompt();

  const showNoLensSelected = !filters.protocol || filters.protocol === "all";
  const hasAttributeExamples = attributeExamples && attributeExamples.length > 0;
  const showEmptyState = !isLoading && !fetchError && !hasAttributeExamples;
  const showGenerateForm = !isLoading && !fetchError && hasAttributeExamples;

  const availableAttributes = useMemo(() => {
    if (!attributeExamples) {
      return [];
    }
    return attributeExamples
      .filter(attributeExample => attributeExample.lowScoring.length > 0 || attributeExample.highScoring.length > 0)
      .map(attributeExample => attributeExample.attribute);
  }, [attributeExamples]);

  useEffect(() => {
    if (!filters.protocol) {
      setCustomPrompt("");
      return;
    }

    const attributeNames = selectedAttributes.size > 0
      ? Array.from(selectedAttributes).join(", ")
      : undefined;
    const defaultPrompt = generateDefaultCoachingPrompt({
      lensName: filters.protocol,
      attributeNames,
      coachingHubPrompt: coachingLensPrompt
    });
    setCustomPrompt(defaultPrompt);
  }, [selectedAttributes, filters.protocol, coachingLensPrompt, setCustomPrompt]);

  useEffect(() => {
    clearSelectedAttributes();
  }, [filters.protocol, clearSelectedAttributes]);

  useEffect(() => {
    setError("");
  }, [filters.protocol, filters.user, setError]);

  useEffect(() => {
    setTemperature(coachingLensTemperature);
  }, [coachingLensTemperature, setTemperature]);

  const handleSelectAll = () => {
    if (availableAttributes.length === 0) {
      return;
    }
    selectAllAttributes(availableAttributes);
  };

  const handleTemperatureChange = (_event: Event, newValue: number | number[]) => {
    setTemperature(newValue as number);
  };

  const checkForDuplicateConfiguration = (): CoachingArtifact | null => {
    if (!filters.user || !filters.protocol || filters.protocol === "all") {
      return null;
    }

    const currentConfig = {
      agent_email: filters.user,
      lens_name: filters.protocol,
      timeRange: filters.timeRange,
      temperature,
      selectedAttributes: Array.from(selectedAttributes).sort(),
      includeAttributeStats,
      includeLensDefinition,
      includeTeamBestPractices,
      customPrompt
    };

    const duplicate = existingArtifacts.find(artifact => {
      const artifactSelectedAttributes = artifact.lens_config?.selectedAttributes
        ? [...artifact.lens_config.selectedAttributes].sort()
        : [];

      return (
        artifact.agent_email === currentConfig.agent_email &&
        artifact.lens_name === currentConfig.lens_name &&
        artifact.source_data_filter?.timeRange === currentConfig.timeRange &&
        artifact.lens_config?.temperature === currentConfig.temperature &&
        JSON.stringify(artifactSelectedAttributes) === JSON.stringify(currentConfig.selectedAttributes) &&
        artifact.lens_config?.includeAttributeStats === currentConfig.includeAttributeStats &&
        artifact.lens_config?.includeLensDefinition === currentConfig.includeLensDefinition &&
        artifact.lens_config?.includeTeamBestPractices === currentConfig.includeTeamBestPractices &&
        artifact.lens_config?.customPrompt === currentConfig.customPrompt
      );
    });

    return duplicate || null;
  };

  const executeGeneration = async () => {
    if (!attributeExamples || selectedAttributes.size === 0) {
      return;
    }
    if (!filters.user || !filters.protocol || filters.protocol === "all") {
      return;
    }

    const selectedAttributeExamples = attributeExamples.filter(attributeExample =>
      selectedAttributes.has(attributeExample.attribute)
    );

    setError("");

    try {
      const artifactId = await generateInsights({
        attributeExamples: selectedAttributeExamples,
        attributeStatsContent,
        lensDefinitionContent,
        teamBestPracticesContent,
        selectedUserEmail: filters.user,
        lensName: filters.protocol,
        timeRange: filters.timeRange,
        timezone,
        tenantId,
        coachingLensPrompt,
        coachingLensTitle,
        coachingLensModel,
        coachingLensTemperature,
        coachingLensMaxTokens
      });

      queryClient.invalidateQueries({ queryKey: ["coaching-artifacts"] });

      if (artifactId) {
        onClose();
        onArtifactGenerated?.(artifactId);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
    }
  };

  const handleGenerateTrainingInsights = async () => {
    if (!attributeExamples || selectedAttributes.size === 0) {
      return;
    }

    const duplicate = checkForDuplicateConfiguration();
    if (duplicate) {
      setDuplicateArtifact(duplicate);
      setShowDuplicateDialog(true);
      return;
    }

    await executeGeneration();
  };

  const handleConfirmRegenerate = async () => {
    setShowDuplicateDialog(false);
    setDuplicateArtifact(null);
    await executeGeneration();
  };

  const handleCancelRegenerate = () => {
    setShowDuplicateDialog(false);
    setDuplicateArtifact(null);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "80vw" },
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column"
          }
        }}
      >
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0
        }}>
          <Typography variant="h6">Generate Training Insights</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          {showNoLensSelected && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Alert severity="info">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Please select a lens to view coaching insights
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Use the lens filter above to select a specific lens for personalized coaching recommendations.
                </Typography>
              </Alert>
            </Paper>
          )}

          {!showNoLensSelected && (
            <>
              {isLoading && (
                <Paper sx={{ p: 3, mt: 3, display: "flex", justifyContent: "center" }}>
                  <CircularProgress />
                </Paper>
              )}

              {fetchError && (
                <Paper sx={{ p: 3, mt: 3 }}>
                  <Alert severity="error">
                    Failed to load coaching insights: {fetchError.message}
                  </Alert>
                </Paper>
              )}

              {showEmptyState && (
                <Paper sx={{ p: 3, mt: 3 }}>
                  <Alert severity="info">
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      No coaching data available
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      There is no performance data available for the selected filters. Try adjusting your time range or lens selection.
                    </Typography>
                  </Alert>
                </Paper>
              )}

              {showGenerateForm && (
                <Box sx={{ mt: 3 }}>
                  <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 3 }}>
                      <Box sx={{ flex: "0 1 60%", minWidth: 0 }}>
                        <Typography variant="h6" gutterBottom>
                          Training Insights for {filters.protocol}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Select attributes below to generate personalized training insights.
                          Each attribute shows examples of what you are doing well and areas for improvement.
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-end", flexShrink: 0 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedAttributes.size > 0 && selectedAttributes.size === availableAttributes.length}
                              indeterminate={selectedAttributes.size > 0 && selectedAttributes.size < availableAttributes.length}
                              onChange={handleSelectAll}
                            />
                          }
                          label="Select All"
                        />
                        <Button
                          variant="contained"
                          startIcon={isGeneratingInsights || isLoadingContext ? <CircularProgress size={16} /> : <Psychology />}
                          onClick={handleGenerateTrainingInsights}
                          disabled={selectedAttributes.size === 0 || isGeneratingInsights || isLoadingContext || showContextUnavailableWarning}
                        >
                          {isGeneratingInsights
                            ? "Generating..."
                            : isLoadingContext
                              ? "Loading Data..."
                              : `Generate Training Insights (${selectedAttributes.size})`
                          }
                        </Button>
                      </Box>
                    </Box>

                    <CoachingConfiguration
                      attributeStatsContent={attributeStatsContent}
                      lensDefinitionContent={lensDefinitionContent}
                      teamBestPracticesContent={teamBestPracticesContent}
                      onTemperatureChange={handleTemperatureChange}
                    />
                  </Paper>

                  {showContextUnavailableWarning && (
                    <ContextWarningAlert missingContextNames={missingContextNames} sx={{ mb: 3 }} />
                  )}

                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  {attributeExamples.map((example) => (
                    <CoachingAttributeCard
                      key={example.attribute}
                      attributeExamples={example}
                    />
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      <DuplicateConfigurationDialog
        open={showDuplicateDialog}
        artifact={duplicateArtifact}
        onConfirm={handleConfirmRegenerate}
        onCancel={handleCancelRegenerate}
      />
    </>
  );
};

export default GenerateInsightsDrawer;
